import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import useFetch, { Status } from '../../../../../hooks/fetch.hook';
import MunicipalityApiModel from '../../../models/municipality.api-model';
import type { IMunicipality } from '../../../../../models/municipality.model';
import { CompetitionApiModel } from '../../../models/competition.api-model';
import type ICompetition from '../../../../../models/pa-egna-ben/competition.model';

function processReceivedCompetitions(data: CompetitionApiModel[]): ICompetition[] {
  return data?.map((d) => CompetitionApiModel.mapToBusinessModel(d)) ?? [];
}

function processReceivedParticipatingMunicipalities(data: MunicipalityApiModel[]): IMunicipality[] {
  const result = data?.map((d) => MunicipalityApiModel.mapToBusinessModel(d)) ?? [];
  result.sort((a, b) => a.name.localeCompare(b.name));
  return result;
}

function Statistics() {
  // === STATE ===
  const [competitionsResult] = useFetch<CompetitionApiModel[]>('/api/paegnaben/admin/competitions');
  const [getParticipatingMunicipalitiesResult] = useFetch<MunicipalityApiModel[]>('/api/paegnaben/admin/municipalities/participating');
  const [selectedCompetitionState, setSelectedCompetitionState] = useState<number>(0);
  const [selectedMunicipalityState, setSelectedMunicipalityState] = useState<string>('');
  const [selectedStatisticsTypeState, setSelectedStatisticsTypeState] = useState<number>(1);

  // === VARIABLES ===
  const competitions = processReceivedCompetitions(competitionsResult.data ?? []);
  const participatingMunicipalities = processReceivedParticipatingMunicipalities(getParticipatingMunicipalitiesResult.data ?? []);

  // === HELPERS ===
  function getUrlSelectedStatisticsType(): string {
    switch (selectedStatisticsTypeState) {
      case 1:
        return `/api/paegnaben/admin/statistics/${selectedCompetitionState}/contactinformation/${selectedMunicipalityState}`;
      case 2:
        return `/api/paegnaben/admin/statistics/${selectedCompetitionState}/participationspermunicipality`;
      case 3:
        return `/api/paegnaben/admin/statistics/${selectedCompetitionState}/participationspermonth/${selectedMunicipalityState}`;
      case 4:
        return `/api/paegnaben/admin/statistics/${selectedCompetitionState}/exercise/${selectedMunicipalityState}`;
      case 5:
        return `/api/paegnaben/admin/statistics/${selectedCompetitionState}/score/${selectedMunicipalityState}`;
      default:
        return '';
    }
  }

  // === EVENT HANDLERS ===
  async function handleDownloadStatisticsButtonClick(): Promise<void> {
    window.open(getUrlSelectedStatisticsType(), '_blank');
  }

  if (selectedCompetitionState === 0 && competitions.length > 0) {
    setSelectedCompetitionState(competitions[0].id);
  }

  // === RENDER ===
  if (competitionsResult.status === Status.failed && competitionsResult.httpResponseStatusCode === 401) {
    return <Navigate to="/session-utgangen" />;
  }
  if (getParticipatingMunicipalitiesResult.status === Status.failed && getParticipatingMunicipalitiesResult.httpResponseStatusCode === 401) {
    return <Navigate to="/session-utgangen" />;
  }
  if (competitionsResult.status === Status.failed && competitionsResult.httpResponseStatusCode === 403) {
    return <Navigate to="/atkomst-saknas" />;
  }
  if (getParticipatingMunicipalitiesResult.status === Status.failed && getParticipatingMunicipalitiesResult.httpResponseStatusCode === 403) {
    return <Navigate to="/atkomst-saknas" />;
  }

  return (
    <form>
      <h1>Statistik</h1>
      <div className="row mb-2">
        <div className="col col-md-6">
          <label htmlFor="competition" className="form-label">
            Tävling
          </label>
          <select id="competition" className="form-select" value={selectedCompetitionState} onChange={(x) => setSelectedCompetitionState(parseInt(x.target.value, 10))}>
            {competitions?.map((competition) => (
              <option key={competition.id} value={competition.id}>
                {competition.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      {selectedStatisticsTypeState !== 2 && (
        <div className="row mb-2">
          <div className="col col-md-6">
            <label htmlFor="municipality" className="form-label">
              Kommun
            </label>
            <select id="municipality" className="form-select" value={selectedMunicipalityState} onChange={(x) => setSelectedMunicipalityState(x.target.value)}>
              <option value={''}>Alla kommuner</option>
              {participatingMunicipalities?.map((municipality) => (
                <option key={municipality.gid} value={municipality.gid}>
                  {municipality.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
      <div className="row mb-4">
        <div className="col col-md-6">
          <label htmlFor="statisticsType" className="form-label">
            Typ av statistik
          </label>
          <select id="statisticsType" className="form-select" value={selectedStatisticsTypeState} onChange={(x) => setSelectedStatisticsTypeState(parseInt(x.target.value, 10))}>
            <option value="1">Adressuppgifter till klasser</option>
            <option value="2">Klasser per kommuner</option>
            <option value="5">Poängställning och tävlingsrapport</option>
            <option value="3">Registreringar</option>
            <option value="4">Övningar</option>
          </select>
        </div>
      </div>
      <div className="row">
        <div className="col col-md-6">
          <button type="button" className="btn btn-primary" onClick={handleDownloadStatisticsButtonClick}>
            Hämta statistik
          </button>
        </div>
      </div>
    </form>
  );
}

export default Statistics;
